import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TextField, { TextFieldProps } from "@material-ui/core/TextField";

import { bankAccountsSelector } from "src/store/bankAccount/selector";
import AddBankAccount from "src/pages/Reconciliation/AddBankAccount";

import Autocomplete from "./Autocomplete";

interface SalePlatformValue {
  id: string;
  name: string;
}
export interface SalePlatformFieldProps {
  label?: string | null;
  value?: Partial<SalePlatformValue>;
  onChange: (salePLatform: SalePlatformValue) => void;
  type?: "all" | "sale-platform" | "bank-account";
}

const AddNew = {
  id: "add-new",
  name: "Add Sale Platform",
};

function SalePlatformField({
  label = "Sale Platform",
  value,
  onChange,
  disabled,
  type = "sale-platform",
  ...props
}: SalePlatformFieldProps &
  Partial<Omit<TextFieldProps, "value" | "label" | "onChange">>) {
  const bankAccounts = useSelector(bankAccountsSelector);
  const valueId = value?.id;
  const valueName = value?.name;
  const platforms = useMemo(() => {
    const platforms: SalePlatformValue[] = bankAccounts.filter(
      (ba) =>
        !ba.hidden &&
        (type === "all"
          ? true
          : type === "sale-platform"
          ? ba.type === "sale-platform"
          : ba.type !== "sale-platform")
    );
    platforms.sort((a, b) => a.name.localeCompare(b.name));

    if (!valueId && valueName) {
      const platform = platforms.find((p) => p.name === valueName);
      if (!platform) {
        platforms.unshift({ id: "unknown", name: valueName });
      }
    }
    return [AddNew, ...platforms];
  }, [bankAccounts, type, valueId, valueName]);
  const val = useMemo(() => {
    if (!valueId && !valueName) return null;
    return {
      id: valueId
        ? valueId
        : platforms.find((p) => p.name === valueName)?.id || "unknown",
      name: valueName
        ? valueName
        : platforms.find((p) => p.id === valueId)?.name || "",
    };
  }, [platforms, valueId, valueName]);
  const [open, setOpen] = useState(false);

  return (
    <>
      <AddBankAccount
        open={open}
        type={type === "sale-platform" ? "sale-platform" : "bank-account"}
        onClose={(salePlatform) => {
          setOpen(false);
          if (salePlatform) onChange(salePlatform);
        }}
      />
      <Autocomplete
        fullWidth
        autoSelect
        value={val}
        disabled={disabled}
        options={platforms}
        getOptionLabel={(opt) =>
          opt.id === "add-new" ? `Add ${label}` : opt?.name || ""
        }
        renderInput={(params) => (
          <TextField label={label} variant="outlined" {...props} {...params} />
        )}
        getOptionSelected={(a, b) => a?.id === b?.id}
        getOptionDisabled={(option) => option?.id === "unknown"}
        onChange={(e, opt) => {
          const value = opt?.id;
          if (value === "add-new") {
            setOpen(true);
            return;
          }

          if (
            !opt ||
            !opt?.name ||
            !value ||
            value === "default" ||
            value === "unkown"
          ) {
            return;
          }

          onChange(opt as SalePlatformValue);
        }}
      />
    </>
  );
}

export default SalePlatformField;
